import { useState } from 'react';
import { RecordingControl } from './components/RecordingControl';
import { DashboardTab } from './components/tabs/DashboardTab';
import { CustomPredictTab } from './components/tabs/CustomPredictTab';
import { PipelineTestTab } from './components/tabs/PipelineTestTab';
import { SavedDataTab } from './components/tabs/SavedDataTab';
import { JsonPipelineTab } from './components/tabs/JsonPipelineTab';
import { useQuery } from '@tanstack/react-query';
import { ediphorDiagnosticsService } from '@/services/api/ediphorDiagnosticsService';

type MainTab = 'dashboard' | 'custom' | 'pipeline' | 'saved' | 'json-pipeline';

export function ClassificationDiagnosticsPage() {
  const [activeTab, setActiveTab] = useState<MainTab>('dashboard');

  // Query for system health check (used in header)
  const { data: tokenInfo } = useQuery({
    queryKey: ['ediphor-token-info'],
    queryFn: () => ediphorDiagnosticsService.getTokenInfo(),
    refetchInterval: 10000,
    retry: 1
  });

  // Calculate system health
  const isHealthy = tokenInfo?.accessToken.isValid && tokenInfo?.refreshToken.isPresent;

  const tabConfig = [
    { id: 'dashboard' as MainTab, label: 'Dashboard', icon: '🏠' },
    { id: 'custom' as MainTab, label: 'Custom Predict', icon: '📝' },
    { id: 'pipeline' as MainTab, label: 'Pipeline Test', icon: '🔬' },
    { id: 'json-pipeline' as MainTab, label: 'JSON Pipeline', icon: '📄' },
    { id: 'saved' as MainTab, label: 'Saved Data', icon: '💾' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-900 dark:to-blue-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 text-white">Classification Service Diagnostics</h1>
              <p className="text-blue-100 text-sm">Monitor and test your Ediphor integration</p>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
              isHealthy 
                ? 'bg-green-500 bg-opacity-20 text-green-100' 
                : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <span className={`w-3 h-3 rounded-full ${isHealthy ? 'bg-green-400' : 'bg-red-400'} animate-pulse`} />
              <span className="text-sm font-medium">{isHealthy ? 'System Healthy' : 'Authentication Required'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recording Control - Sticky */}
      <div className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <RecordingControl />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-0" aria-label="Main tabs">
            {tabConfig.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-6 font-medium text-sm transition-colors border-b-2 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-gray-700'
                    : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {activeTab === 'dashboard' && <DashboardTab onNavigateToTab={setActiveTab} />}
          {activeTab === 'custom' && <CustomPredictTab />}
          {activeTab === 'pipeline' && <PipelineTestTab />}
          {activeTab === 'json-pipeline' && <JsonPipelineTab />}
          {activeTab === 'saved' && <SavedDataTab />}
        </div>
      </div>
    </div>
  );
}
